import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  ChevronRight,
  Search,
  Filter,
  Plus,
  MessageSquare,
  Package,
  TrendingUp,
  AlertCircle,
} from 'lucide-react';
import { useCountry } from '../context/CountryContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getStatusBadge = (status) => {
  const statusConfig = {
    pending: { label: 'Pending', className: 'bg-yellow-100 text-yellow-700' },
    quoting: { label: 'Receiving Quotes', className: 'bg-blue-100 text-blue-700' },
    awarded: { label: 'Awarded', className: 'bg-green-100 text-green-700' },
    booked: { label: 'Booked', className: 'bg-purple-100 text-purple-700' },
    completed: { label: 'Completed', className: 'bg-gray-100 text-gray-700' },
    cancelled: { label: 'Cancelled', className: 'bg-red-100 text-red-700' },
  };
  
  const config = statusConfig[status] || { label: status, className: 'bg-gray-100 text-gray-700' };
  return <Badge className={config.className}>{config.label}</Badge>;
};

const RFQCard = ({ rfq, onViewQuotes }) => {
  const navigate = useNavigate();
  const quotesCount = rfq.quotes_received || 0;
  const shortlistedCount = rfq.quotes?.filter(q => q.shortlisted)?.length || 0;
  
  return (
    <Card className="hover:shadow-md transition-shadow cursor-pointer" data-testid={`rfq-card-${rfq.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-mono text-sm text-gray-500">{rfq.rfq_number}</span>
              {getStatusBadge(rfq.status)}
            </div>
            <h3 className="font-semibold text-lg">{rfq.title}</h3>
            <p className="text-sm text-gray-500 line-clamp-2">{rfq.item_details}</p>
          </div>
        </div>
        
        <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
          <span className="flex items-center gap-1">
            <Package className="w-4 h-4" />
            {rfq.quantity} {rfq.quantity_unit}
          </span>
          <span className="flex items-center gap-1">
            <Clock className="w-4 h-4" />
            {new Date(rfq.created_at).toLocaleDateString()}
          </span>
        </div>
        
        <div className="flex items-center justify-between pt-3 border-t">
          <div className="flex items-center gap-3">
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-600">{quotesCount}</p>
              <p className="text-xs text-gray-500">Quotes</p>
            </div>
            {shortlistedCount > 0 && (
              <div className="text-center">
                <p className="text-2xl font-bold text-green-600">{shortlistedCount}</p>
                <p className="text-xs text-gray-500">Shortlisted</p>
              </div>
            )}
          </div>
          
          <div className="flex gap-2">
            {quotesCount > 0 && (
              <Button 
                size="sm" 
                onClick={(e) => { e.stopPropagation(); onViewQuotes(rfq.id); }}
                data-testid={`view-quotes-${rfq.id}`}
              >
                View Quotes
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            )}
            {quotesCount === 0 && rfq.status === 'pending' && (
              <Badge variant="outline" className="text-yellow-600">
                <Clock className="w-3 h-3 mr-1" />
                Awaiting Quotes
              </Badge>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default function MyRFQsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { country } = useCountry();
  
  const [rfqs, setRfqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [stats, setStats] = useState({ total: 0, pending: 0, quoting: 0, awarded: 0 });
  
  // Get user email from localStorage or URL param
  const userEmail = searchParams.get('email') || localStorage.getItem('userEmail') || '';
  
  useEffect(() => {
    const fetchRFQs = async () => {
      if (!userEmail) {
        setLoading(false);
        return;
      }
      
      setLoading(true);
      try {
        const response = await fetch(
          `${API_URL}/api/business-services/rfq?customer_email=${encodeURIComponent(userEmail)}&country=${country}`
        );
        const data = await response.json();
        
        if (data.success) {
          setRfqs(data.rfqs || []);
          
          // Calculate stats
          const rfqList = data.rfqs || [];
          setStats({
            total: rfqList.length,
            pending: rfqList.filter(r => r.status === 'pending').length,
            quoting: rfqList.filter(r => r.status === 'quoting').length,
            awarded: rfqList.filter(r => r.status === 'awarded' || r.status === 'booked').length,
          });
        }
      } catch (error) {
        console.error('Failed to fetch RFQs:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchRFQs();
  }, [userEmail, country]);
  
  const filteredRfqs = rfqs.filter(rfq => {
    const matchesSearch = !searchTerm || 
      rfq.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      rfq.rfq_number?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || rfq.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });
  
  const handleViewQuotes = (rfqId) => {
    navigate(`/rfq/${rfqId}/quotes`);
  };
  
  if (!userEmail) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-4xl mx-auto">
          <Card className="p-8 text-center">
            <AlertCircle className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <h2 className="text-xl font-semibold mb-2">Please Sign In</h2>
            <p className="text-gray-500 mb-4">You need to be signed in to view your RFQs.</p>
            <Button onClick={() => navigate('/login')}>Sign In</Button>
          </Card>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50 p-6" data-testid="my-rfqs-page">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">My RFQs</h1>
            <p className="text-gray-500">Track your quote requests and manage supplier quotes</p>
          </div>
          <Button onClick={() => navigate('/category/manufacturing-supplies')} data-testid="new-rfq-btn">
            <Plus className="w-4 h-4 mr-2" />
            New RFQ
          </Button>
        </div>
        
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4 text-center">
              <FileText className="w-8 h-8 mx-auto mb-2 text-blue-500" />
              <p className="text-2xl font-bold">{stats.total}</p>
              <p className="text-sm text-gray-500">Total RFQs</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <Clock className="w-8 h-8 mx-auto mb-2 text-yellow-500" />
              <p className="text-2xl font-bold">{stats.pending}</p>
              <p className="text-sm text-gray-500">Pending</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <MessageSquare className="w-8 h-8 mx-auto mb-2 text-purple-500" />
              <p className="text-2xl font-bold">{stats.quoting}</p>
              <p className="text-sm text-gray-500">Receiving Quotes</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-500" />
              <p className="text-2xl font-bold">{stats.awarded}</p>
              <p className="text-sm text-gray-500">Awarded</p>
            </CardContent>
          </Card>
        </div>
        
        {/* Search & Filter */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search by title or RFQ number..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="rfq-search"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40" data-testid="status-filter">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="quoting">Receiving Quotes</SelectItem>
              <SelectItem value="awarded">Awarded</SelectItem>
              <SelectItem value="booked">Booked</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        {/* RFQ List */}
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <Card key={i} className="animate-pulse">
                <CardContent className="p-4">
                  <div className="h-6 bg-gray-200 rounded w-1/4 mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : filteredRfqs.length > 0 ? (
          <div className="space-y-4">
            {filteredRfqs.map(rfq => (
              <RFQCard 
                key={rfq.id} 
                rfq={rfq} 
                onViewQuotes={handleViewQuotes}
              />
            ))}
          </div>
        ) : (
          <Card className="p-12 text-center">
            <FileText className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-semibold mb-2">No RFQs Found</h3>
            <p className="text-gray-500 mb-4">
              {searchTerm || statusFilter !== 'all' 
                ? 'Try adjusting your search or filters'
                : 'You haven\'t submitted any RFQs yet'}
            </p>
            <Button onClick={() => navigate('/category/manufacturing-supplies')}>
              <Plus className="w-4 h-4 mr-2" />
              Create Your First RFQ
            </Button>
          </Card>
        )}
      </div>
    </div>
  );
}
